import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../services/api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const getLocation = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      return (
        [data.city, data.region, data.country_name]
          .filter(Boolean)
          .join(", ") || null
      );
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const location = await getLocation();
      await api.post("/contact", { ...formData, location });
      toast.success("Message sent! We will get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Failed to send message. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = [
    {
      icon: <Phone size={22} />,
      title: "Phone",
      content: "+91 8218579930\n+91 9068676184",
    },
    {
      icon: <MapPin size={22} />,
      title: "Address",
      content: "Buranskhanda, Near Dhanaulti, Uttrakhand 248001",
    },
    {
      icon: <Mail size={22} />,
      title: "Email",
      content: "suryaprataps375@gmail.com",
    },
    {
      icon: <Clock size={22} />,
      title: "Working Hours",
      content: "Mon - Sun: 8:00 AM - 10:00 PM",
    },
  ];

  // Styles
  const sectionStyle = { padding: "80px 24px" };
  const containerStyle = { maxWidth: 1200, margin: "0 auto" };
  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: 20,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    overflow: "hidden",
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
  };
  const btnPrimaryStyle = {
    backgroundColor: "#16a34a",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: 12,
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
    transition: "transform 0.2s",
    opacity: submitting ? 0.7 : 1,
  };

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          height: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&h=800&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(22,163,74,0.8) 0%, rgba(21,128,61,0.9) 100%)",
          }}
        ></div>
        <div
          style={{
            position: "relative",
            zIndex: 10,
            textAlign: "center",
            color: "#fff",
            padding: "0 24px",
            animation: "fadeInUp 0.8s ease-out",
          }}
        >
          <p
            style={{
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 6,
              fontWeight: "bolder",
              color: "rgba(255,255,255,0.7)",
              marginBottom: 12,
            }}
          >
            Reach Out
          </p>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(40px, 6vw, 56px)",
              fontWeight: 900,
              marginBottom: 16,
            }}
          >
            Contact Us
          </h1>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 17 }}>
            We'd love to hear from you. Get in touch with us!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={containerStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: 48,
            }}
          >
            {/* Info */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  color: "#16a34a",
                  textTransform: "uppercase",
                  letterSpacing: 4,
                  fontWeight: 600,
                  marginBottom: 12,
                }}
              >
                Get In Touch
              </p>
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 40,
                  fontWeight: 900,
                  color: "#1f2937",
                  lineHeight: 1.1,
                  marginBottom: 16,
                }}
              >
                We're Here
                <br />
                to Help
              </h2>
              <div
                style={{
                  width: 56,
                  height: 4,
                  background: "#16a34a",
                  borderRadius: 2,
                  marginBottom: 32,
                }}
              ></div>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: 17,
                  lineHeight: 1.7,
                  marginBottom: 40,
                }}
              >
                Have questions about your stay? We're here to help. Reach out
                through any of these channels.
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                {contactItems.map(({ icon, title, content }) => (
                  <div
                    key={title}
                    style={{
                      display: "flex",
                      gap: 16,
                      padding: 20,
                      backgroundColor: "#fff",
                      borderRadius: 20,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #e5e7eb",
                      transition: "border-color 0.2s",
                    }}
                  >
                    <div
                      style={{
                        color: "#16a34a",
                        marginTop: 2,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        backgroundColor: "rgba(22,163,74,0.1)",
                        padding: 10,
                        borderRadius: 12,
                        alignSelf: "flex-start",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {icon}
                    </div>
                    <div>
                      <h4
                        style={{
                          fontWeight: 600,
                          color: "#1f2937",
                          marginBottom: 4,
                          fontSize: 14,
                        }}
                      >
                        {title}
                      </h4>
                      <p
                        style={{
                          color: "#6b7280",
                          fontSize: 13,
                          lineHeight: 1.6,
                          whiteSpace: "pre-line",
                          margin: 0,
                        }}
                      >
                        {content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div style={{ ...cardStyle, border: "1px solid #e5e7eb" }}>
              <div style={{ padding: 32 }}>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#1f2937",
                    marginBottom: 24,
                  }}
                >
                  Send Us a Message
                </h3>
                <form
                  onSubmit={handleSubmit}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          paddingBottom: 6,
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#374151",
                          }}
                        >
                          Your Name
                        </span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter your name"
                        style={inputStyle}
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          paddingBottom: 6,
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#374151",
                          }}
                        >
                          Email Address
                        </span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        style={inputStyle}
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          paddingBottom: 6,
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#374151",
                          }}
                        >
                          Phone Number
                        </span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Your phone number"
                        style={inputStyle}
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    {/* <div>
                      <label
                        style={{
                          display: "block",
                          paddingBottom: 6,
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#374151",
                          }}
                        >
                          Subject
                        </span>
                      </label>
                      <input
                        type="text"
                        name="subject"
                        placeholder="What is this regarding?"
                        style={inputStyle}
                        value={formData.subject}
                        onChange={handleChange}
                      />
                    </div> */}
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        paddingBottom: 6,
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: "#374151",
                        }}
                      >
                        Message
                      </span>
                    </label>
                    <textarea
                      name="message"
                      placeholder="Write your message here..."
                      style={{
                        ...inputStyle,
                        minHeight: 128,
                        resize: "vertical",
                      }}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={btnPrimaryStyle}
                  >
                    {submitting ? (
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "#fff",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></span>
                    ) : (
                      <Send size={18} />
                    )}
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section>
        <iframe
          title="Burans Hikes location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3441.096816051585!2d78.23079937612283!3d30.404994901397053!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3908df832df12c43%3A0x5e23a2aba0682be3!2sBurans%20Hikes%20Home%20Stay%20(Resort)!5e0!3m2!1sen!2sin!4v1781382293190!5m2!1sen!2sin"
          width="100%"
          height="450"
          style={{ border: 0, display: "block" }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>
    </div>
  );
};

export default Contact;
